"use client";

import { useEffect, useState } from "react";

import { useRomaReserveSheet } from "@/components/roma/roma-reserve-sheet-context";
import type { RomaReserveLocation } from "@/components/roma/roma-types";

type RomaDarkFunnelMobileBarProps = {
  locations: readonly RomaReserveLocation[];
};

/** Fixed bottom Reserve bar — mock `mobile-cta`; opens the Roma location sheet. */
export function RomaDarkFunnelMobileBar({
  locations,
}: RomaDarkFunnelMobileBarProps) {
  const reserveSheet = useRomaReserveSheet();
  const [hasPassedBoundary, setHasPassedBoundary] = useState(false);
  const [footerInView, setFooterInView] = useState(false);

  useEffect(() => {
    const boundary = document.getElementById("home-mobile-sticky-boundary");
    const footer = document.querySelector("footer");
    let frame = 0;

    const updateVisibility = () => {
      const boundaryRect = boundary?.getBoundingClientRect();
      const footerRect = footer?.getBoundingClientRect();

      setHasPassedBoundary(boundaryRect ? boundaryRect.top < 0 : true);
      setFooterInView(
        footerRect
          ? footerRect.top < window.innerHeight && footerRect.bottom > 0
          : false,
      );
    };

    const scheduleUpdate = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        updateVisibility();
      });
    };

    updateVisibility();
    window.addEventListener("scroll", scheduleUpdate, { passive: true });
    window.addEventListener("resize", scheduleUpdate);

    return () => {
      if (frame) {
        window.cancelAnimationFrame(frame);
      }
      window.removeEventListener("scroll", scheduleUpdate);
      window.removeEventListener("resize", scheduleUpdate);
    };
  }, []);

  const visible = hasPassedBoundary && !footerInView;

  if (locations.length === 0) {
    return null;
  }

  return (
    <>
      <nav
        aria-label="Roma quick reserve"
        aria-hidden={!visible}
        // `inert` keeps the Reserve button out of the tab order while the bar
        // is aria-hidden (pointer-events-none alone leaves it keyboard-focusable,
        // which fails "ARIA hidden element must not contain focusable elements").
        inert={!visible}
        data-funnel-priority="mobile-sticky-actions"
        className={`fixed inset-x-0 bottom-0 z-40 border-t border-white/10 bg-[#0d0d0d]/95 px-4 pb-[calc(env(safe-area-inset-bottom)+0.75rem)] pt-3 shadow-[0_-16px_48px_-32px_rgba(0,0,0,0.85)] backdrop-blur transition-[transform,opacity] duration-300 ease-out md:hidden ${
          visible
            ? "translate-y-0 opacity-100"
            : "pointer-events-none translate-y-full opacity-0"
        }`}
      >
        <button
          type="button"
          className="roma-funnel-btn-primary flex min-h-12 w-full items-center justify-center rounded-lg border border-[#b91c1c] bg-[#b91c1c] px-5 text-[15px] font-semibold text-white transition-colors hover:border-[#cf1f1f] hover:bg-[#cf1f1f]"
          onClick={() => reserveSheet?.openSheet()}
          data-track="reservation_clicked"
          data-track-location="home_dark_funnel_mobile_sticky"
        >
          Reserve
        </button>
      </nav>
    </>
  );
}
